import type { ImageRecord } from './imageDb'
import { maxAreaForAspect, normRot } from './orientation'

type SizeLike = {
  title?: string | null
  widthMm?: number
  heightMm?: number
  /** Beschnitt umlaufend in mm; > 0 => der Crop deckt Endformat + 2 x Beschnitt ab */
  bleedMm?: number | null
  geometry?: { bleedMm?: number | null } | null
} | null | undefined

/**
 * Beschnitt einer Größe in mm (0 = keiner). Quelle: size.bleedMm, sonst size.geometry.bleedMm.
 */
export function resolveSizeBleedMm(size: SizeLike): number {
  const direct = Number(size?.bleedMm)
  if (Number.isFinite(direct) && direct > 0) return direct
  const nested = Number(size?.geometry?.bleedMm)
  return Number.isFinite(nested) && nested > 0 ? nested : 0
}

function normalizeMm(value: unknown): number {
  const num = Number(value || 0)
  if (!Number.isFinite(num) || num <= 0) return 0
  return Math.round(num)
}

function parseSizeTitle(size: SizeLike): { widthMm: number; heightMm: number } | null {
  const title = String(size?.title || '').trim()
  if (!title) return null
  const match = title.match(/(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)/i)
  if (!match) return null

  const widthMm = Math.round(parseFloat(String(match[1]).replace(',', '.')) * 10)
  const heightMm = Math.round(parseFloat(String(match[2]).replace(',', '.')) * 10)
  if (!widthMm || !heightMm) return null

  return { widthMm, heightMm }
}

function resolveSizeMm(size: SizeLike): { widthMm: number; heightMm: number } | null {
  const widthMm = normalizeMm(size?.widthMm)
  const heightMm = normalizeMm(size?.heightMm)
  if (widthMm > 0 && heightMm > 0) return { widthMm, heightMm }
  return parseSizeTitle(size)
}

function normalizeRotation(value: unknown): number {
  return normRot(value)
}

/**
 * Seitenverhältnis des Crops: bei Beschnitt das volle Format (Endformat + 2 x Beschnitt),
 * damit der gespeicherte Ausschnitt exakt der Druckdatei entspricht.
 */
export function sizeAspectWithBleed(widthMm: number, heightMm: number, bleedMm: number): number {
  const b = Math.max(0, Number(bleedMm) || 0)
  const w = widthMm + 2 * b
  const h = heightMm + 2 * b
  return h > 0 ? w / h : 0
}

function resolveCropBoxDims(rec: Pick<ImageRecord, 'width' | 'height' | 'edits'> & Record<string, any>) {
  const dims = resolveEditorDims(rec)
  const width = dims.width
  const height = dims.height
  const rot = normalizeRotation((rec.edits as any)?.rotate ?? 0)
  const boxW = rot === 90 || rot === 270 ? height : width
  const boxH = rot === 90 || rot === 270 ? width : height
  return { width, height, boxW, boxH, rot }
}

function shouldSwapAspectForRecord(
  rec: Pick<ImageRecord, 'width' | 'height' | 'edits'> & Record<string, any>,
  sizeAr: number,
): boolean {
  const { boxW, boxH } = resolveCropBoxDims(rec)
  if (!(boxW > 0) || !(boxH > 0) || !(sizeAr > 0) || !Number.isFinite(sizeAr)) return false
  return maxAreaForAspect(boxW, boxH, 1 / sizeAr) > maxAreaForAspect(boxW, boxH, sizeAr)
}

export function resolveEffectiveAspectRatioForRecord(
  rec: Pick<ImageRecord, 'width' | 'height' | 'edits'> & Record<string, any>,
  size: SizeLike,
): number | undefined {
  const sizeMm = resolveSizeMm(size)
  if (!sizeMm) return undefined

  let ar = sizeAspectWithBleed(sizeMm.widthMm, sizeMm.heightMm, resolveSizeBleedMm(size))
  if (!Number.isFinite(ar) || ar <= 0) return undefined

  const swapAR = !!((rec.edits as any)?.swapAR)
  const orientMode = String((rec.edits as any)?.orientMode || 'auto')

  if (orientMode === 'auto') {
    if (shouldSwapAspectForRecord(rec, ar)) ar = 1 / ar
  }

  if (swapAR) ar = 1 / ar

  return ar > 0 && Number.isFinite(ar) ? ar : undefined
}

function resolveEditorDims(rec: Pick<ImageRecord, 'width' | 'height' | 'edits'> & Record<string, any>) {
  const width = Math.max(1, Math.round(Number(rec.editorWidth || rec.width || rec.thumbWidth || 0)))
  const height = Math.max(1, Math.round(Number(rec.editorHeight || rec.height || rec.thumbHeight || 0)))
  return { width, height }
}

export function buildMaxCropForRecord(
  rec: Pick<ImageRecord, 'width' | 'height' | 'edits'> & Record<string, any>,
  size: SizeLike,
): { x: number; y: number; w: number; h: number; sourceWidth: number; sourceHeight: number; nx: number; ny: number; nw: number; nh: number } | undefined {
  const dims = resolveCropBoxDims(rec)
  const width = dims.width
  const height = dims.height
  if (!width || !height) return undefined

  const boxW = dims.boxW
  const boxH = dims.boxH

  const sizeMm = resolveSizeMm(size)
  if (!sizeMm) {
    return { x: 0, y: 0, w: boxW, h: boxH, sourceWidth: width, sourceHeight: height, nx: 0, ny: 0, nw: boxW / width, nh: boxH / height }
  }

  const ar = resolveEffectiveAspectRatioForRecord(rec, size)
  if (!ar) {
    return { x: 0, y: 0, w: boxW, h: boxH, sourceWidth: width, sourceHeight: height, nx: 0, ny: 0, nw: boxW / width, nh: boxH / height }
  }

  const imageAr = boxW / boxH
  let cropW = boxW
  let cropH = boxH

  if (imageAr >= ar) {
    cropH = boxH
    cropW = Math.round(cropH * ar)
  } else {
    cropW = boxW
    cropH = Math.round(cropW / ar)
  }

  cropW = Math.max(2, Math.min(boxW, cropW))
  cropH = Math.max(2, Math.min(boxH, cropH))

  const x = Math.round((boxW - cropW) / 2)
  const y = Math.round((boxH - cropH) / 2)

  return {
    x,
    y,
    w: cropW,
    h: cropH,
    sourceWidth: width,
    sourceHeight: height,
    nx: Math.max(0, Math.min(1, x / width)),
    ny: Math.max(0, Math.min(1, y / height)),
    nw: Math.max(0, Math.min(1, cropW / width)),
    nh: Math.max(0, Math.min(1, cropH / height)),
  }
}
